import { createTheme, PaletteMode, ThemeOptions } from "@mui/material";

export const getThemeConfig = (
  mode: PaletteMode,
  language = "en",
): ThemeOptions => {
  const isKhmer = language === "kh";
  const isDark = mode === "dark";

  // E-Theap Website Brand Color Palette (Luxury Wedding Gold & Warm Obsidian/Pearl)
  const darkColors = {
    primary: "#C48C3A", // E-Theap Signature Gold
    primaryLight: "#DDB068",
    primaryDark: "#9E6F24",
    secondary: "#D4A373", // Warm Champagne Bronze
    secondaryLight: "#F5EEDB",
    secondaryDark: "#A67247",
    background: {
      default: "#181716", // Warm Obsidian Dark
      paper: "#242220", // Card Surface
      elevated: "#2E2B27", // Elevated Surface
    },
    text: {
      primary: "#F7F5F0", // Warm Pearl
      secondary: "#B8B2A7", // Soft Linen
      disabled: "#757067",
    },
    divider: "rgba(255, 255, 255, 0.08)",
    border: "#3D3A35",
    error: "#E55353",
    success: "#4CAF50",
    warning: "#F59E0B",
    info: "#C48C3A",
  };

  const lightColors = {
    primary: "#C48C3A", // E-Theap Signature Gold
    primaryLight: "#DDB068",
    primaryDark: "#9E6F24",
    secondary: "#A67247",
    secondaryLight: "#D4A373",
    secondaryDark: "#7A4E2B",
    background: {
      default: "#FAF8F5", // Warm Linen/Pearl Off-White
      paper: "#FFFFFF",
      elevated: "#F3EFEA",
    },
    text: {
      primary: "#2C2722", // Dark Charcoal
      secondary: "#6B6359", // Warm Taupe
      disabled: "#A0988C",
    },
    divider: "rgba(0, 0, 0, 0.08)",
    border: "#E8E2D9",
    error: "#BA1A1A",
    success: "#2E7D32",
    warning: "#ED6C02",
    info: "#0277BD",
  };

  const colors = isDark ? darkColors : lightColors;

  const fontFamily = isKhmer
    ? `"Noto Sans Khmer", "Kantumruy Pro", "Koh Santepheap", system-ui, -apple-system, sans-serif`
    : `"Montserrat", "Inter", system-ui, -apple-system, BlinkMacSystemFont, sans-serif`;

  const headingFontFamily = isKhmer
    ? `"Noto Sans Khmer", "Kantumruy Pro", "Koh Santepheap", system-ui, -apple-system, sans-serif`
    : `"Playfair Display", "Cinzel", "Montserrat", Georgia, serif`;

  return {
    palette: {
      mode,
      primary: {
        main: colors.primary,
        light: colors.primaryLight,
        dark: colors.primaryDark,
        contrastText: "#FFFFFF",
      },
      secondary: {
        main: colors.secondary,
        light: colors.secondaryLight,
        dark: colors.secondaryDark,
        contrastText: "#FFFFFF",
      },
      error: {
        main: colors.error,
      },
      success: {
        main: colors.success,
      },
      warning: {
        main: colors.warning,
      },
      info: {
        main: colors.info,
      },
      background: {
        default: colors.background.default,
        paper: colors.background.paper,
      },
      text: {
        primary: colors.text.primary,
        secondary: colors.text.secondary,
        disabled: colors.text.disabled,
      },
      divider: colors.divider,
    },
    typography: {
      fontFamily,
      h1: {
        fontFamily: headingFontFamily,
        fontWeight: 700,
        lineHeight: isKhmer ? 1.85 : 1.2,
      },
      h2: {
        fontFamily: headingFontFamily,
        fontWeight: 700,
        lineHeight: isKhmer ? 1.85 : 1.25,
      },
      h3: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
        lineHeight: isKhmer ? 1.9 : 1.3,
      },
      h4: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
        lineHeight: isKhmer ? 1.9 : 1.35,
      },
      h5: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
        lineHeight: isKhmer ? 1.95 : 1.4,
      },
      h6: {
        fontFamily: headingFontFamily,
        fontWeight: 600,
        lineHeight: isKhmer ? 1.95 : 1.5,
      },
      subtitle1: {
        fontWeight: 600,
        lineHeight: isKhmer ? 1.8 : 1.5,
      },
      subtitle2: {
        fontWeight: 600,
        lineHeight: isKhmer ? 1.75 : 1.45,
      },
      button: {
        fontWeight: 600,
        textTransform: "none",
      },
    },
    shape: {
      borderRadius: 10,
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: colors.background.default,
            color: colors.text.primary,
            fontFamily,
            userSelect: "none",
            WebkitUserSelect: "none",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? "rgba(36, 34, 32, 0.92)"
              : "rgba(255, 255, 255, 0.92)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            backgroundImage: "none",
            borderBottom: `1px solid ${colors.divider}`,
            color: colors.text.primary,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundImage: "none",
            backgroundColor: colors.background.paper,
            border: `1px solid ${colors.divider}`,
            boxShadow: isDark
              ? "0 4px 16px -2px rgba(0, 0, 0, 0.3)"
              : "0 2px 12px -2px rgba(196, 140, 58, 0.08)",
            transition: "all 0.2s ease-in-out",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 10,
            border: "none",
            boxShadow: "none",
            transition: "all 0.15s ease-in-out",
          },
          contained: {
            boxShadow: "none",
            border: "none",
            "&:hover": {
              boxShadow: "none",
            },
            "&:active": {
              boxShadow: "none",
            },
          },
          containedPrimary: {
            background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
            color: "#FFFFFF",
            "&:hover": {
              background: `linear-gradient(135deg, #E5BD7A 0%, #D49B45 100%)`,
            },
          },
          outlined: {
            border: "none",
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.06)"
              : "rgba(0, 0, 0, 0.04)",
            color: colors.text.primary,
            "&:hover": {
              border: "none",
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.1)"
                : "rgba(0, 0, 0, 0.07)",
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: colors.primary,
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.9rem",
            "&.Mui-selected": {
              color: colors.primary,
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            color: colors.primary,
            textDecorationColor: colors.primary,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: colors.divider,
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
            borderRadius: 10,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
            "& fieldset": {
              border: "none",
            },
            "&:hover fieldset": {
              border: "none",
            },
            "&.Mui-focused fieldset": {
              border: `1.5px solid ${colors.primary}`,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            backgroundColor: isDark
              ? "rgba(255, 255, 255, 0.05)"
              : "rgba(0, 0, 0, 0.03)",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.05)",
            },
            "&.Mui-focused": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(0, 0, 0, 0.05)",
            },
            "&:before, &:after": {
              display: "none",
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 0,
            transition: "all 0.15s ease-in-out",
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(196, 140, 58, 0.08)"
                : "rgba(196, 140, 58, 0.05)",
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            "&.Mui-checked": {
              color: colors.primary,
              "& + .MuiSwitch-track": {
                backgroundColor: colors.primary,
                opacity: 0.6,
              },
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: colors.primary,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            "&.Mui-checked": {
              color: colors.primary,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
          },
          colorPrimary: {
            backgroundColor: isDark
              ? "rgba(196, 140, 58, 0.2)"
              : "rgba(196, 140, 58, 0.12)",
            color: colors.primary,
            border: `1px solid rgba(196, 140, 58, 0.3)`,
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 10,
            transition: "all 0.2s ease",
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.paper,
            borderRadius: 12,
            border: `1px solid ${colors.divider}`,
            boxShadow: isDark
              ? "0 4px 16px -2px rgba(0, 0, 0, 0.25)"
              : "0 2px 8px -1px rgba(196, 140, 58, 0.08)",
            padding: 4,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            border: "none",
            textTransform: "none",
            fontWeight: 600,
            fontSize: "0.85rem",
            color: colors.text.secondary,
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&.Mui-selected": {
              background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%) !important`,
              color: "#FFFFFF !important",
              boxShadow: "0 2px 8px 0 rgba(196, 140, 58, 0.35)",
            },
            "&:hover": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.06)"
                : "rgba(196, 140, 58, 0.08)",
            },
          },
        },
      },
    },
  };
};

export const createAppTheme = (mode: PaletteMode, language = "en") => {
  return createTheme(getThemeConfig(mode, language));
};
